<template>
  <div :key="$i18n.locale" v-bind="{ ...$props, ...$attrs }" class="rich-text-input-container">
    <!-- Label + optional tooltip -->
    <label v-if="label" class="v-label">
      {{ label }}
      <help-icon-tooltip v-if="tooltipText" iconStyleClass="grey darken-2">
        {{ tooltipText }}
      </help-icon-tooltip>
    </label>

    <template v-if="editorComponent && editor">
      <component
        :is="editorComponent"
        :editor="editor"
        :model-value="normalizedModelValue"
        :config="localEditorConfig"
        @input="onInput"
        @ready="onReady" />
    </template>
    <v-progress-linear v-else indeterminate color="primary" />
  </div>
</template>

<script>
/*
  RichTextInput.vue
  CKEditor is loaded lazily -- only fetched when this component is first mounted.
  Example usage: <rich-text-input v-model="text" label="My Label" />
*/
import { shallowRef } from 'vue';
import { merge } from 'lodash';
import { useLocaleHelpers } from '@satellite/composables/useLocaleHelpers.js';

const LOCALE_IMPORTS = {
  es: () => import('@ckeditor/ckeditor5-build-classic/build/translations/es.js'),
  'pt-br': () => import('@ckeditor/ckeditor5-build-classic/build/translations/pt-br.js'),
  fr: () => import('@ckeditor/ckeditor5-build-classic/build/translations/fr.js')
};

let loadPromise = null;
let cachedEditor = null;
let cachedComponent = null;

function loadCKEditor() {
  if (!loadPromise) {
    loadPromise = Promise.all([
      import('@ckeditor/ckeditor5-build-classic'),
      import('@ckeditor/ckeditor5-vue')
    ]).then(([editorModule, vueModule]) => {
      cachedEditor = editorModule.default;
      cachedComponent = vueModule.Ckeditor;
    });
  }
  return loadPromise;
}

export default {
  name: 'RichTextInput',
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    tooltipText: {
      type: String,
      default: null
    },
    editorConfig: {
      type: Object,
      default() {
        return {};
      }
    }
  },
  setup() {
    const { getBaseLanguageCodeFromLocale } = useLocaleHelpers();
    const editor = shallowRef(null);
    const editorComponent = shallowRef(null);

    return {
      editor,
      editorComponent,
      getBaseLanguageCodeFromLocale
    };
  },
  data() {
    return {
      isInitialized: false
    };
  },
  computed: {
    normalizedModelValue() {
      return this.modelValue == null ? '' : String(this.modelValue);
    },
    localEditorConfig() {
      const defaultConfig = {
        licenseKey: 'GPL',
        toolbar: {
          items: ['bold', 'italic', 'link', 'undo', 'redo']
        },
        link: {
          addTargetToExternalLinks: true,
          defaultProtocol: 'https://'
        },
        language: this.getBaseLanguageCodeFromLocale(this.$i18n.locale)
      };
      return merge({}, defaultConfig, this.editorConfig);
    }
  },
  async mounted() {
    const lang = this.getBaseLanguageCodeFromLocale(this.$i18n.locale);
    const localeLoader = LOCALE_IMPORTS[lang];

    await Promise.all([loadCKEditor(), localeLoader?.()].filter(Boolean));

    this.editor = cachedEditor;
    this.editorComponent = cachedComponent;
  },
  methods: {
    onReady(editorInstance) {
      this.$emit('ready', editorInstance);
      if (!this.isInitialized) {
        const editable = editorInstance.ui.getEditableElement();
        const toolbarEl = editorInstance.ui.view.toolbar.element;
        editable.parentElement.insertBefore(toolbarEl, editable);
        this.isInitialized = true;
      }
    },
    onInput(newValue) {
      this.$emit('update:modelValue', newValue);
    }
  },
  watch: {
    modelValue(newVal) {
      if (newVal === null || typeof newVal === 'undefined') {
        this.$emit('update:modelValue', '');
      }
    }
  }
};
</script>

<style scoped lang="scss">
.rich-text-input-container {
  .ck-content {
    border: 1px solid #777 !important;
    border-radius: 0 0 5px 5px !important;
    border-top: none !important;
  }

  .ck-toolbar {
    border: 1px solid #777 !important;
    border-radius: 5px 5px 0 0 !important;
  }

  /* Hide the "Powered by CKEditor" branding if desired */
  .ck-powered-by {
    display: none !important;
  }
}
</style>
